<template>
  <main>
    <big-loader v-if="isLoading"/>
    <div id="wrapper" ref="wrapper" v-else>

      <TableToolbar
          :id="'trendingtoolbar'"
          :buttons="['info']"
          :info="dictionary.messages.trendingStocks">
        <template #main>
          <div>&nbsp;</div>
        </template>
      </TableToolbar>

      <DataGridTable :is-compact="true"
                     :key="tableKey"
                     :data="trends"
                     :hovered-columns="true"
                     :loaded="isLoading"
                     :height-decrease="15"
                     :default-sort="defaultSort"
                     :extraHeaders="extraHeaders"
                     :heightElements="['trendingtoolbar']"
                     :default-sort-direction="defaultSortDirection"
                     :cols="[]"
                     :fixed-col="0">
        <template
            #thead="{thClick, sortKey, sortDirection, xAxisScrolled, toggleSymbolActions, showSymbolActions, headers}">
          <HeadCell v-for="(th, thIndex) in headers"
                    :key="thIndex + 'th'"
                    :sortKey="sortKey"
                    :header="th"
                    :sortDirection="sortDirection"
                    :sortValue="th.sortValue"
                    :showSymbolActions="showSymbolActions"
                    @toggleSymbolActions="toggleSymbolActions()"
                    @sort="thClick(th.sortValue, thIndex);">
            {{ th.label }}
          </HeadCell>
        </template>

        <template #tbody="{rows, showSymbolActions, cols}">
          <tr v-for="(row, rowIndex) in rows" :key="`r-${rowIndex}`">
            <td>
              <symbol-tag :tags="row.tags" :options="{link: true, chart: true, logo: 'small', hideTrendingTag: true}"
                          :showSymbolActions="showSymbolActions" :symbol="row.symbol"/>
            </td>
            <td class="bidi">{{ row.mentions }}</td>
            <td class="bidi">{{ row.rankChange }}</td>
            <td>
              <symbol-label :key="`s-${row.symbol}`"
                            :pre-market-quote="row.tradingData.preMarket"
                            :post-market-quote="row.tradingData.postMarket"
                            :onlyPostMarket="activeButton === 'postMarket'"
                            :onlyPreMarket="activeButton === 'preMarket'"
                            :symbol="row.tradingData" :price-only="true"
                            :force-pre-post="activeButton !== 'tradingHours'"
                            :hide-pre-post="activeButton === 'tradingHours'"
                            :show-prePost="activeButton !== 'tradingHours'" :always-on="true"/>
            </td>
   <VolumeCell :volume="row.tradingData?.formattedVolume" :trend="row.tradingData?.volumeTrend" />
            <td>{{ row.tradingData.formattedMarketCap }}</td>
       <NumericCell
                :value="row.tradingData?.atr?.atrPercent"
                :ranges="[0, 1, 3, 5, 7]"
                :classes="['low','moderate','medium','high','extreme']"
            />
            <TrendIconCell :trend="row.tradingData?.atr?.atrTrend"/>
            <TrendIconCell :trend="row.tradingData?.obvTrend"/>
            <NumericCell v-for="period in ['5D','10D','1M']" :key="period + 'nc'" :value="row.eods?.[period]"/>

            <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3"
                           :previousClose="row.tradingData?.price" :direction="'top'"
                           :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma50"
                           @deltaChange="deltaChange(row.symbol, $event, 'ma50D')"/>

            <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3"
                           :previousClose="row.tradingData?.price" :direction="'bottom'"
                           :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma200"
                           @deltaChange="deltaChange(row.symbol, $event, 'ma200D')"/>

            <YearlyRangeCell :delta-change="this.deltaChange" :row="row"/>
            <RSICell :rsi="row.tradingData?.rsi"/>
            <MACDCell :macd="row.tradingData?.macd"/>
     
            <NumericCell :noColor="true" :value="row.tradingData?.eps"/>
            <NumericCell :noColor="true" :value="row.tradingData?.pe"/>
            <IndustrySectorCell :symbol="row"/>
          </tr>
        </template>
      </DataGridTable>
    </div>

  </main>
</template>

<script>
import NewsService from '../services/news';
import SocialTrend from '../services/socialBuzz';
import SymbolLabel from "../components/symbols/symbol";
import BigLoader from "../components/ui/BigLoader";
import UserService from "../services/user";
import {useWatchlist} from "../stores/watchlist";
import SortHoldings from "../components/sectors/SortHoldings";
import TableSortTH from "../components/ui/TableSortTH";
import SymbolLink from "../components/symbols/symbolLink";


import quoteStreaming from "../composables/quoteStreaming";
import DataGridTable from "../components/dataGrid/DataGridTable";
import HeadCell from "../components/dataGrid/HeadCell";
import SymbolLogo from "../components/symbols/symbolLogo";
import TableToolbar from "../components/ui/TableToolbar";
import PriceDistance from "../components/priceTags/priceDistance";
import SymbolChart from "../components/symbols/symbolChart";
import SymbolTag from "../components/symbols/symbolTag";
import PriceRange from "../components/priceTags/priceRange";
import NumericCell from "../components/dataGrid/NumericCell";
import YearlyRangeCell from "../components/dataGrid/YearlyRangeCell";
import RSICell from "../components/dataGrid/RSICell";
import MACDCell from "../components/dataGrid/MACDCell";
import TrendIconCell from "../components/dataGrid/TrendIconCell";
import IndustrySectorCell from "../components/dataGrid/IndustrySectorCell";
import VolumeCell from "../components/dataGrid/VolumeCell";

export default {
  name: "SocialTrend",
  props: ['prices', 'isPartial', 'isMini'],
  components: {
    IndustrySectorCell,
    VolumeCell,
    TrendIconCell,
    MACDCell,
    RSICell,
    YearlyRangeCell,
    NumericCell,
    PriceRange,
    SymbolTag,
    SymbolChart,
    PriceDistance,
    TableToolbar,
    SymbolLogo, HeadCell, DataGridTable, SymbolLink, TableSortTH, SortHoldings, BigLoader, SymbolLabel
  },
  setup() {

    const {updateStream, leaveStream} = quoteStreaming();
    return {updateStream, leaveStream};
  },
  data() {
    return {
      tableKey: 0,
      extraHeaders: [
        {
          position: 1,
          label: this.dictionary.trendingStocks.numberOfMentions,
          sortValue: 'mentions'
        }, {
          position: 2,
          label: this.dictionary.trendingStocks.ratingChange,
          sortValue: 'rankChange'
        }],
      defaultSort: 'rank',
      defaultSortDirection: 1,
      etf: {
        he: 'תעודת סל',
        en: 'ETF'
      },
      watchlist: useWatchlist(),
      isLoading: true,
      trends: []
    }
  },
  computed: {
    list() {

      return this.sortByKey(this.trends);
    },
    periods() {

      return ['5D', '1M', '6M'].map(period => {

        return this.dictionary.search.filter.periods.find(dPeriod => dPeriod.value === period)
      });
    }
  },
  methods: {
    deltaChange(symbol, value, key) {

      this.trends = this.trends.map(row => {

        if (row.symbol === symbol) {

          row[key] = value;
        }
        return row;
      });
    },
    toTrendingStocks() {
      this.$router.push('/social');
      this.$router.go(1);
      this.$emit('close');
    },
    columnHover(cIdx) {
      this.compareColumn = cIdx;
    },
    scrolled(ev) {

      const scrollLeftPosition = ev.target.scrollLeft;
      this.xAxisScrolled = Math.abs(scrollLeftPosition) > 10;
    },
    formatSectorIndustry(symbol) {

      if (symbol.name.includes('ETF')) return this.etf[this.systemLanguage];
      if (symbol.sector !== '-' && symbol.industry === '-') return `${symbol.sector}`
      if (symbol.sector === '-' && symbol.industry === '-') return `-`
      return symbol.industry;
      return `${symbol.sector} - ${symbol.industry}`
    },
    async createFeed() {

      this.isLoading = true;
      const feedId = await NewsService.createFilteredFeed({type: 'trending'});
      this.$router.push(`/feed/filtered/${feedId.feedId}`);
      this.$router.go(1);

    },
    priceSortValue() {
      return this.activeButton === 'tradingHours' ? 'changeP' : this.activeButton === 'preMarket' ? 'tradingData.preMarket.changeP' : 'tradingData.postMarket.changeP';
    },
    setChangeColor(change) {
      return change > 0 ? 'green' : change < 0 ? 'red' : 'black';
    },
    async addRemoveWatchlistSymbol(symbol) {

      if (this.watchlist.watchlist.includes(symbol)) {

        try {
          mixpanel.track('symbol:remove', {client: window.location.host, symbol: symbol});
        } catch (e) {
        }

        const watchlist = await UserService.removeSymbolFromWatchlist(symbol)
        this.watchlist.updateWatchlist(watchlist);
      } else {

        try {
          mixpanel.track('symbol:add', {client: window.location.host, symbol: symbol});
        } catch (e) {
        }
        const watchlist = await UserService.addSymbolToWatchlist(symbol)
        this.watchlist.updateWatchlist(watchlist);
      }
    },
    async loadSocialTrends() {

      this.leaveStream('social');
      this.isLoading = true;
      this.trends = (await SocialTrend.getSocialTrend()).splice(0, this.isMini ? 20 : 100);
      if (this.isPartial) this.trends = this.trends.splice(0, 10);
      this.isLoading = false;

      this.updateStream(this.trends.map(trend => trend.symbol), 'social');


      this.$nextTick(() => {

        // if (window.innerWidth <= 660) {

        const navBar = document.querySelector('#navBar');
        const explP = this.$refs.expl;
        const filterNews = this.$refs.filterNews;

        const heightOfDiv1 = navBar.clientHeight;
        const heightOfDiv2 = 0 //explP.clientHeight;
        const heightOfDiv3 = window.innerWidth <= 660 ? filterNews.clientHeight : 5;

        const maxHeightDifference = heightOfDiv1 + heightOfDiv2 + heightOfDiv3;
        this.$refs.wrapper.style.height = `${window.innerHeight - maxHeightDifference - 5}px`
        // }
      });
    }
  },
  mounted() {

  },
  beforeUnmount() {
    this.leaveStream('social');
  },
  async beforeMount() {
    await this.loadSocialTrends();
  }
}
</script>

<style scoped lang="scss">
@import "../assets/style/calendar";
@import "../assets/style/vars";


.calendarHeadersRow {
  top: 0;
}

.cell {
  width: 20%;
  position: relative;
  white-space: pre;
  flex-shrink: 0;
  font-size: 14px;

  &.min {
    width: 10%;
  }

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
}

#wrapper {
  //@media(max-width: 660px) {
  overflow: auto;
  white-space: nowrap;
  //}
}


.relative {
  position: relative;

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

.companyName {
  width: fit-content;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  div {
    overflow: hidden;
    white-space: nowrap;
    width: 200px;
    text-overflow: ellipsis;
  }
}

.cell[dir="rtl"] {
  unicode-bidi: plaintext;
  direction: rtl;
}

.cell.period {
  width: fit-content;
}

.center {
  text-align: center;
}

.justify {
  justify-content: center;
  display: flex;
}




.priceChange.green, .priceChange.red {
  background: var(--bgColor);
}

#filterNews {
  padding: 1rem 0;
  display: flex;
  justify-content: center;
  @media(min-width: 661px) {
    display: none;
  }
}

button {
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  background: var(--highlightColor);
  color: var(--bgColor);
  cursor: pointer;
  padding: 5px 23px;
}

#seeFullList {
  font-size: 18px;
  display: block;
  margin: 3rem auto;
}

.expl {
  white-space: normal;
  position: sticky;
  left: 5px;
}

.sideBorder {
  border-right: 1px solid var(--tableBorderColor);

  &[dir="rtl"] {
    border-right: unset;
    border-left: 1px solid var(--tableBorderColor);
  }
}

.stickySymbol {
  background: var(--bgColor);
  position: sticky;
  left: 0;
  border-right: 1px solid var(--tableBorderColor);
  z-index: 2;

  &[dir="rtl"] {
    border-right: unset;
    border-left: 1px solid var(--tableBorderColor);
    left: unset;
    right: 0;
  }
}

.xAxisScrolled {
  box-shadow: 3px 0 2px 0 rgba(0, 0, 0, 0.05);
  border-right: 1px solid var(--notficationBorderColor);

  &[dir="rtl"] {
    border-right: unset;
    border-left: 1px solid var(--notficationBorderColor);
    box-shadow: -3px 0 2px 0 rgba(0, 0, 0, 0.05);
  }
}

.rankingCell {
  position: relative;

  div {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
}

.addRemoveSymbol {
  z-index: 1;
}


</style>
